import React, { useContext, useEffect, useState, useRef } from 'react'
import './Navbar.css'
import { assets } from '../../assets/assets'
import { Link, useNavigate } from 'react-router-dom'
import { StoreContext } from '../../Context/StoreContext'
import { useMultiCart } from '../../Context/MultiCartContext'
import { useAuth } from '../../Context/AuthContext'
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher'
import { useTranslation } from 'react-i18next'

const Navbar = ({ setShowProfileModal }) => {
  const { t } = useTranslation();

  const [menu, setMenu] = useState("home");
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [deliveryOption, setDeliveryOption] = useState("Delivery");
  
  const { getTotalCartAmount, token, setToken, profilePicture, username, getFormattedAddress, userAddress, notificationCount, activeOrderCount, otherNotificationCount, isTokenLoading, isUserDataLoading, updateOtherNotificationCount, clearUserData } = useContext(StoreContext);
  

  const { restaurantCarts, getTotalItemCount } = useMultiCart();
  const { triggerLogin } = useAuth();
  
  const navigate = useNavigate();
  const profileDropdownRef = useRef(null);
  const sidebarRef = useRef(null);

  // Get total cart count (number of restaurant carts)
  const getCartCount = () => {
    return restaurantCarts ? restaurantCarts.length : 0;
  };

  // Get total item count across all carts  
  const getTotalItems = () => {
    if (restaurantCarts && restaurantCarts.length > 0) {
      return getTotalItemCount();
    }
    return getTotalCartAmount() > 0 ? 1 : 0; // Fallback to old cart system
  };

  // Close dropdown and sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setShowSidebar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const logout = () => {
    localStorage.removeItem("token");
    setToken("");
    clearUserData();
    navigate('/')
    setShowSidebar(false);
  }

  const openProfileModal = () => {
    if (setShowProfileModal) {
      setShowProfileModal(true);
      setShowSidebar(false);
    }
  };

  const toggleSidebar = () => {
    if (!isTokenLoading && !isUserDataLoading && token && username) {
      setShowSidebar(prev => !prev);
    }
  };

  const scrollToMenu = () => {
    
    // First try to scroll to popular dishes
    const popularDishes = document.getElementById('popular-dishes');
    if (popularDishes) {
      const yOffset = -80; // Account for fixed navbar
      const y = popularDishes.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      return;
    }
    
    // If popular dishes not found, scroll to restaurant selector
    const restaurantSelector = document.getElementById('find-restaurant');
    if (restaurantSelector) {
      const yOffset = -80; // Account for fixed navbar
      const y = restaurantSelector.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      return;
    }
    
    // If neither found, scroll to food display
    const foodDisplay = document.getElementById('food-display');
    if (foodDisplay) {
      const yOffset = -80; // Account for fixed navbar
      const y = foodDisplay.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      console.log('No target elements found for scrolling');
    }
  };

  return (
    <>
      <div className='navbar'>
        <div className='navbar-content'>
          {/* Left side - Logo and Menu */}
          <div className='navbar-left'>
            <Link to='/'><img className='logo' src={assets.logo2} alt="DASHBASKET" /></Link>
                         <div className="navbar-menu-items">
               <Link to="/" onClick={() => setMenu("home")} className={`${menu === "home" ? "active" : ""}`}>{t('nav.home')}</Link>
               <button 
                 onClick={() => {
                   setMenu("menu");
                   scrollToMenu();
                 }} 
                 className={`navbar-menu-button ${menu === "menu" ? "active" : ""}`}
                 style={{ 
                   WebkitTapHighlightColor: 'transparent',
                   touchAction: 'manipulation',
                   userSelect: 'none'
                 }}
               >
                 {t('nav.menu')}
               </button>
             </div>
          </div>

          {/* Right side - Cart, Profile (Language switcher moved to center on mobile) */}
          <div className="navbar-right">
            {/* Language Switcher - Desktop only */}
            <div className="navbar-language desktop-only">
              <LanguageSwitcher showLabel={false} variant="dropdown" />
            </div>
            
            {/* Cart - Only show when signed in on mobile */}
            {token && (
              <Link to='/cart' className='navbar-cart'>
                <img src={assets.basket_icon} alt="Cart" />
                {getCartCount() > 0 && (
                  <div className='cart-count'>{getCartCount() > 99 ? '99+' : getCartCount()}</div>
                )}
              </Link>
            )}

            {/* Profile/Login */}
            {isTokenLoading || isUserDataLoading ? (
              <div className='profile-menu-trigger'>
                <div className='profile-shimmer'></div>
              </div>
                         ) : !token ? (
               <button className='sign-in-btn' onClick={triggerLogin}>{t('nav.sign_in')}</button>
             ) : token && username ? (
              <div className='profile-menu-trigger' onClick={toggleSidebar}>
                {profilePicture ? (
                  <div className='profile-pic-container'>
                    <img src={profilePicture} alt={username || 'Profile'} className='profile-pic' />
                    {notificationCount > 0 && (
                      <div className='profile-notification-count'>{notificationCount > 99 ? '99+' : notificationCount}</div>
                    )}
                  </div>
                ) : (
                  <div className='profile-icon-container'>
                    <div className='profile-icon'>
                      {username.charAt(0).toUpperCase()}
                    </div>
                    {notificationCount > 0 && (
                      <div className='profile-notification-count'>{notificationCount > 99 ? '99+' : notificationCount}</div>
                    )}
                  </div>
                )}
                             </div>
             ) : (
               <button className='sign-in-btn' onClick={triggerLogin}>{t('nav.sign_in')}</button>
             )}
          </div>
          
          {/* Center - Address and Language Switcher (mobile) */}
          <div className='navbar-center'>
            <div className='address-selector'>
              <span className='address-icon'>📍</span>
              <div className='address-info'>
                {isTokenLoading || isUserDataLoading ? (
                  <span className='address-shimmer'></span>
                ) : (
                                     <span className='address-text'>{
                     token && getFormattedAddress() 
                       ? getFormattedAddress() 
                       : t('nav.no_address_found')
                   }</span>
                )}
              </div>
            </div>
            
            {/* Language Switcher - Mobile only */}
            <div className="navbar-language mobile-only">
              <LanguageSwitcher showLabel={false} variant="dropdown" />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar menu */}
      {showSidebar && !isTokenLoading && !isUserDataLoading && token && username && (
        <div className='mobile-sidebar' ref={sidebarRef}>
          <div className='sidebar-header'>
            <div className='sidebar-user-info'>
              {profilePicture ? (
                <img src={profilePicture} alt={username || 'Profile'} className='sidebar-profile-pic' />
              ) : (
                <div className='sidebar-profile-icon'>
                  {username.charAt(0).toUpperCase()}
                </div>
              )}
              <div className='sidebar-username'>{username}</div>
            </div>
            <button className='sidebar-close' onClick={() => setShowSidebar(false)}>✕</button>
          </div>
          
                     <div className='sidebar-menu'>
             <Link to="/" className='sidebar-item' onClick={() => setShowSidebar(false)}>
               {t('nav.home')}
             </Link>
             <Link to="/myorders" className='sidebar-item' onClick={() => setShowSidebar(false)}>
               {t('nav.orders')}
               {activeOrderCount > 0 && (
                 <div className='sidebar-notification-count'>{activeOrderCount > 99 ? '99+' : activeOrderCount}</div>
               )}
             </Link>
             <div className='sidebar-item sidebar-notifications' onClick={() => {
               // TODO: Implement notifications view
               console.log('Notifications clicked');
               // Clear other notifications when clicked
               updateOtherNotificationCount(0);
               setShowSidebar(false);
             }}>
               <span>{t('nav.notifications')}</span>
               {otherNotificationCount > 0 && (
                 <div className='sidebar-notification-count'>{otherNotificationCount > 99 ? '99+' : otherNotificationCount}</div>
               )}
             </div>
             <div className='sidebar-item' onClick={openProfileModal}>
               {t('nav.profile')}
             </div>
             <div className='sidebar-item' onClick={logout}>
               {t('nav.logout')}
             </div>
           </div>
        </div>
      )}
    </>
  )
}

export default Navbar
